/**
 * PATH: packages/email-hub/src/feature/reader-header/model/buildReaderContext.ts
 * FILE_ID: EMAILHUB-READERHEADER-MODEL-CONTEXT-001
 * PURPOSE: Compress reader metadata into a calm, bounded summary.
 * ROLE IN ARCHITECTURE: Pure domain selector for the collapsed triage line.
 * MAIN EXPORTS: buildReaderContext.
 * NON-RESPONSIBILITIES: Expanded lanes keep ownership of editing and detailed labels.
 * NOTES FOR FUTURE AI: Summary tokens are bounded; never map every tag or placement into the header.
 */
import type { Email } from '../../hooks/useEmails'
import { selectReaderPlacements } from './placements'
import type { ReaderContextSummary } from './types'

function humanize(value: string | null | undefined): string | null {
  if (!value?.trim()) return null
  return value
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase())
}

/**
 * Convert an email payload into at most five summary tokens.
 *
 * Inputs: read-only Email fields. Output: bounded text/counts for the collapsed
 * header. Side effects: none. Missing fields are omitted without placeholders.
 */
export function buildReaderContext(
  email: Pick<
    Email,
    | 'from_address'
    | 'company_label'
    | 'supplier_name'
    | 'client_name'
    | 'teammate_name'
    | 'category'
    | 'cost_category_label'
    | 'tags'
    | 'ai_summary'
    | 'allocation_summary'
    | 'allocation_summaries'
    | 'watchers'
  >,
): ReaderContextSummary {
  const company =
    email.company_label ||
    email.supplier_name ||
    email.client_name ||
    email.teammate_name ||
    null
  const classification =
    humanize(email.category) ?? email.cost_category_label ?? null
  const placements = selectReaderPlacements(email)
  const signalCount =
    (email.tags?.length ?? 0) + (email.ai_summary?.trim() ? 1 : 0)
  const watcherCount = email.watchers?.length ?? 0

  const tokens = [
    company,
    classification,
    email.cost_category_label,
    placements.length > 0
      ? `${placements.length} placement${placements.length === 1 ? '' : 's'}`
      : null,
    signalCount > 0
      ? `${signalCount} signal${signalCount === 1 ? '' : 's'}`
      : null,
  ].filter((token): token is string => Boolean(token))

  return {
    company,
    classification,
    signalCount,
    placementCount: placements.length,
    watcherCount,
    tokens: tokens.slice(0, 5),
  }
}
